/**
 * トーストのキュー（docs/design/components.md §4.3）。同時に 1 つだけ出し、残りは順に待つ。
 * 画面のどこからでも `toasts.show({ message })` で出せる。
 */

export type ToastKind = 'info' | 'ok' | 'warn' | 'err';

export interface ToastAction {
  label: string;
  run(): void;
}

export interface ToastInput {
  message: string;
  kind?: ToastKind;
  action?: ToastAction;
  durationMs?: number;
}

export interface ToastItem {
  id: number;
  message: string;
  kind: ToastKind;
  action?: ToastAction;
  durationMs: number;
}

export const DEFAULT_TOAST_MS = 5000;
export const ACTION_TOAST_MS = 8000;

export function toastDuration(input: {
  action?: ToastAction | undefined;
  durationMs?: number | undefined;
}): number {
  return input.durationMs ?? (input.action ? ACTION_TOAST_MS : DEFAULT_TOAST_MS);
}

export function createToastQueue() {
  let nextId = 1;
  const state = $state<{ items: ToastItem[] }>({ items: [] });

  return {
    get current(): ToastItem | null {
      return state.items[0] ?? null;
    },
    get pending(): number {
      return Math.max(0, state.items.length - 1);
    },
    show(input: ToastInput): number {
      const item: ToastItem = {
        id: nextId++,
        message: input.message,
        kind: input.kind ?? 'info',
        durationMs: toastDuration(input),
      };
      if (input.action) item.action = input.action;
      // 同じ文言が並んでいたら積まない（連打で読み上げを詰まらせない）
      if (!state.items.some((t) => t.message === item.message && t.kind === item.kind)) {
        state.items.push(item);
      }
      return item.id;
    },
    dismiss(id: number): void {
      state.items = state.items.filter((t) => t.id !== id);
    },
    clear(): void {
      state.items = [];
    },
  };
}

export type ToastQueue = ReturnType<typeof createToastQueue>;

/** アプリ全体で共有するキュー */
export const toasts: ToastQueue = createToastQueue();
